import { motion } from "framer-motion";
import { AlertCircle, Brain, Key, Network, Users } from "../components/icons";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { TopCoaches } from "../components/partner/TopCoaches";
import { CodeRedemptionRate } from "../components/partner/CodeRedemptionRate";
import { AssessmentsOverTime } from "../components/partner/AssessmentsOverTime";
import { RecentCompletions } from "../components/partner/RecentCompletions";
import { TeamCompletionSummary } from "../components/partner/TeamCompletionSummary";
import { InactiveCoachesAlert } from "../components/partner/InactiveCoachesAlert";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import {
  fetchTopCoaches,
  fetchCodeRedemption,
  fetchAssessmentsOverTime,
  fetchRecentCompletions,
  fetchTeamCompletions,
  fetchInactiveCoaches,
  type CoachRanking,
  type CodeRedemption,
  type MonthlyAssessment,
  type RecentCompletion,
  type TeamCompletion,
  type InactiveCoach,
} from "../lib/partner-dashboard";

export function PartnerDashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    totalCoaches: 0,
    activeAssessments: 0,
    availableCodes: 0,
    totalTeams: 0,
  });
  const [topCoaches, setTopCoaches] = useState<CoachRanking[]>([]);
  const [codeRedemption, setCodeRedemption] = useState<CodeRedemption>({ totalCodes: 0, usedCodes: 0, rate: 0 });
  const [assessmentsOverTime, setAssessmentsOverTime] = useState<MonthlyAssessment[]>([]);
  const [recentCompletions, setRecentCompletions] = useState<RecentCompletion[]>([]);
  const [teamCompletions, setTeamCompletions] = useState<TeamCompletion[]>([]);
  const [inactiveCoaches, setInactiveCoaches] = useState<InactiveCoach[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    loadDashboardStats();
  }, [user?.id]);

  const loadDashboardStats = async () => {
    try {
      setIsLoading(true);

      if (!user?.id) {
        setError("User not found");
        setIsLoading(false);
        return;
      }

      const { data: partnerData } = await supabase
        .from('partners')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!partnerData) {
        setError("Partner record not found");
        setIsLoading(false);
        return;
      }

      const partnerId = partnerData.id;

      const { count: coachesCount } = await supabase
        .from('coaches')
        .select('*', { count: 'exact', head: true })
        .eq('partner_id', partnerId);

      const { count: codesCount } = await supabase
        .from('assessment_codes')
        .select('*', { count: 'exact', head: true })
        .eq('partner_id', partnerId)
        .eq('is_used', false);

      const { data: coachIds } = await supabase
        .from('coaches')
        .select('id')
        .eq('partner_id', partnerId);

      let assessmentsCount = 0;
      if (coachIds && coachIds.length > 0) {
        const { count } = await supabase
          .from('respondents')
          .select('*', { count: 'exact', head: true })
          .in('coach_id', coachIds.map(c => c.id));

        assessmentsCount = count || 0;
      }

      const { count: teamsCount } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true })
        .eq('partner_id', partnerId);

      setStats({
        totalCoaches: coachesCount || 0,
        activeAssessments: assessmentsCount,
        availableCodes: codesCount || 0,
        totalTeams: teamsCount || 0,
      });

      const [
        topCoachesData,
        codeRedemptionData,
        assessmentsData,
        recentData,
        teamData,
        inactiveData,
      ] = await Promise.all([
        fetchTopCoaches(partnerId),
        fetchCodeRedemption(partnerId),
        fetchAssessmentsOverTime(partnerId),
        fetchRecentCompletions(partnerId),
        fetchTeamCompletions(partnerId),
        fetchInactiveCoaches(partnerId),
      ]);

      setTopCoaches(topCoachesData);
      setCodeRedemption(codeRedemptionData);
      setAssessmentsOverTime(assessmentsData);
      setRecentCompletions(recentData);
      setTeamCompletions(teamData);
      setInactiveCoaches(inactiveData);
    } catch (err: any) {
      console.error("Failed to load dashboard stats:", err);
      setError(err.message || "Failed to load dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading partner dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-brand-5 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-heading-sm text-gray-80">Dashboard</h1>
          <p className="text-text-md text-gray-50">Overview of your coaching network</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-destructive-5 text-destructive-40 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-heading-sm text-gray-80 mb-1">
                  {stats.totalCoaches}
                </div>
                <div className="text-text-sm text-gray-40">Active Coaches</div>
              </div>
              <div className="p-3 bg-success-10 rounded-xl">
                <Users className="w-6 h-6 text-success-40" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-heading-sm text-gray-80 mb-1">
                  {stats.totalTeams}
                </div>
                <div className="text-text-sm text-gray-40">Teams</div>
              </div>
              <div className="p-3 bg-brand-10 rounded-xl">
                <Network className="w-6 h-6 text-brand-40" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-heading-sm text-gray-80 mb-1">
                  {stats.availableCodes}
                </div>
                <div className="text-text-sm text-gray-40">Available Codes</div>
              </div>
              <div className="p-3 bg-orange-10 rounded-xl">
                <Key className="w-6 h-6 text-orange-40" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-heading-sm text-gray-80 mb-1">
                  {stats.activeAssessments}
                </div>
                <div className="text-text-sm text-gray-40">Total Assessments</div>
              </div>
              <div className="p-3 bg-teal-10 rounded-xl">
                <Brain className="w-6 h-6 text-teal-50" />
              </div>
            </div>
          </motion.div>
        </div>

        {inactiveCoaches.length > 0 && (
          <div className="mb-8">
            <InactiveCoachesAlert coaches={inactiveCoaches} />
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <AssessmentsOverTime data={assessmentsOverTime} />
          <CodeRedemptionRate data={codeRedemption} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <TopCoaches coaches={topCoaches} />
          <TeamCompletionSummary teams={teamCompletions} />
        </div>

        <RecentCompletions completions={recentCompletions} />
      </div>
    </div>
  );
}
